import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, createUserDocument } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const AuthContext = createContext();

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isChrome = /Chrome/i.test(navigator.userAgent);

console.log(`Device detection: Mobile: ${isMobile}, Chrome: ${isChrome}`);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  // Ensure we're properly cleaning up subscriptions
  useEffect(() => {
    console.log('Setting up auth listener');
    let unsubscribeAuth;
    
    // Add a small delay for Chrome on mobile
    const setupAuth = () => {
      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        console.log('Auth state changed:', user ? 'User logged in' : 'No user');
        setCurrentUser(user);
        
        let unsubscribeUser = null;
        
        try {
          if (user) {
            const userRef = doc(db, 'users', user.uid);
            unsubscribeUser = onSnapshot(userRef, (doc) => {
              if (doc.exists()) {
                setUserData(doc.data());
                console.log('User data loaded');
              }
              setLoading(false);
            }, (error) => {
              console.error('Error fetching user data:', error);
              setAuthError(error.message);
              setLoading(false);
            });
          } else {
            setUserData(null);
            setLoading(false);
          }
        } catch (error) {
          console.error('Auth context error:', error);
          setAuthError(error.message);
          setLoading(false);
        }
      }, (error) => {
        console.error('Auth state change error:', error);
        setAuthError(error.message);
        setLoading(false);
      });
    };
    
    if (isChrome && isMobile) {
      // Add a small delay for Chrome on mobile
      setTimeout(setupAuth, 500);
    } else {
      setupAuth();
    }

    // Return cleanup function for the auth listener
    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    authError,
    signup: async (email, password, username) => {
      try {
        console.log('Signup attempt');
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await createUserDocument(result.user, { username });
        return result;
      } catch (error) {
        console.error('Signup error:', error);
        setAuthError(error.message);
        throw error;
      }
    },
    login: async (email, password) => {
      try {
        console.log('Login attempt');
        return signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        console.error('Login error:', error);
        setAuthError(error.message);
        throw error;
      }
    },
    logout: async () => {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('Logout error:', error);
        setAuthError(error.message);
        throw error;
      }
    },
    resetPassword: async (email) => {
      try {
        return sendPasswordResetEmail(auth, email);
      } catch (error) {
        console.error('Reset password error:', error);
        setAuthError(error.message);
        throw error;
      }
    },
    clearError: () => setAuthError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {authError && (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          left: '20px', 
          right: '20px',
          backgroundColor: '#f44336',
          color: 'white',
          padding: '10px',
          borderRadius: '4px',
          zIndex: 9999
        }}>
          <p>Error: {authError}</p>
          <button 
            onClick={() => setAuthError(null)}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid white',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
} 